import React, { useState, useEffect } from 'react';

// Define interface for Ad data
interface Ad {
  id: number;
  title: string;
  description: string;
  section: string;
  type: string;
  contentUrl: string;
  linkUrl: string;
}

const AddAdPage: React.FC = () => {
  // Form States
  const [adTitle, setAdTitle] = useState('');
  const [description, setDescription] = useState('');
  const [section, setSection] = useState('bottom');
  const [adType, setAdType] = useState('image');
  const [adFile, setAdFile] = useState<File | null>(null);
  const [adLink, setAdLink] = useState('');
  
  // UI States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adsList, setAdsList] = useState<Ad[]>([]); // To store all ads
  const [editingId, setEditingId] = useState<number | null>(null); // To track edit mode

  // --- 1. Fetch Ads on Load ---
  const fetchAds = async () => {
    try {
      const res = await fetch('http://localhost:2026/api/ads');
      const data = await res.json();
      setAdsList(data);
    } catch (error) {
      console.error("Error fetching ads:", error);
    }
  };

  useEffect(() => {
    fetchAds();
  }, []);

  // --- 2. Handle File Input ---
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAdFile(e.target.files[0]);
    }
  };

  // --- 3. Handle Submit (Create or Update) ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('title', adTitle);
    formData.append('description', description);
    formData.append('section', section);
    formData.append('type', adType);
    formData.append('linkUrl', adLink);

    if (adFile) {
      formData.append('adFile', adFile);
    }

    try {
      let url = 'http://localhost:2026/api/ads';
      let method = 'POST';

      // Agar Edit mode hai, to URL aur Method change karein
      if (editingId) {
        url = `http://localhost:2026/api/ads/${editingId}`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method: method,
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to save ad');

      alert(editingId ? 'Ad updated successfully!' : 'Ad created successfully!');
      resetForm();
      fetchAds(); // Refresh list
    } catch (error) {
      console.error('Error submitting ad:', error);
      alert('Error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- 4. Handle Delete ---
  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this ad?")) return;

    try {
      await fetch(`http://localhost:2026/api/ads/${id}`, { method: 'DELETE' });
      setAdsList(adsList.filter(ad => ad.id !== id)); // Remove from UI locally
    } catch (error) {
      console.error("Error deleting ad:", error);
    }
  };

  // --- 5. Handle Edit Click ---
  const handleEdit = (ad: Ad) => {
    setEditingId(ad.id);
    setAdTitle(ad.title);
    setDescription(ad.description || '');
    setSection(ad.section || 'bottom');
    setAdType(ad.type);
    setAdLink(ad.linkUrl);
    setAdFile(null); // File reset, user can keep old one if not changed
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setAdTitle('');
    setDescription('');
    setSection('bottom');
    setAdFile(null);
    setAdLink('');
    setEditingId(null);
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {editingId ? 'Edit Ad' : 'Create New Ad'}
        </h1>
        {editingId && (
          <button onClick={resetForm} className="text-red-500 underline text-sm">
            Cancel Edit
          </button>
        )}
      </div>

      {/* --- FORM SECTION --- */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 mb-10">
        
        <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
          <label className="block text-gray-700 font-bold mb-2">Where to show this Ad?</label>
          <select
            value={section}
            onChange={(e) => setSection(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md outline-none"
          >
            <option value="bottom">Bottom Section (Below Slider)</option>
            <option value="sidebar">Sidebar Slider (Left Side)</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">Ad Title *</label>
            <input
              type="text"
              value={adTitle}
              onChange={(e) => setAdTitle(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">Target URL *</label>
            <input
              type="text"
              value={adLink}
              onChange={(e) => setAdLink(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            rows={2}
            required={section === 'sidebar'}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
             <label className="block text-gray-700 font-semibold mb-2">Ad Format</label>
             <select
                value={adType}
                onChange={(e) => setAdType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
             >
                <option value="image">Image Banner</option>
             </select>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              {editingId ? 'Change Image (Optional)' : 'Upload Image *'}
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              accept="image/*"
              required={!editingId && adType !== 'link'} // Required only for new ads
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full text-white font-bold py-3 px-4 rounded-md transition-colors ${
            editingId ? 'bg-orange-500 hover:bg-orange-600' : 'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {isSubmitting ? 'Processing...' : (editingId ? 'Update Ad' : 'Publish Ad')}
        </button>
      </form>

      {/* --- LIST SECTION (EDIT/DELETE) --- */}
      <h2 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Existing Ads</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adsList.length === 0 ? (
          <p className="text-gray-500">No ads created yet.</p>
        ) : (
          adsList.map((ad) => (
            <div key={ad.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden relative group">
              {/* Image Preview */}
              <div className="h-40 w-full bg-gray-100 flex items-center justify-center overflow-hidden">
                <img 
                  src={`http://localhost:2026${ad.contentUrl}`} 
                  alt={ad.title} 
                  className="object-cover w-full h-full"
                  onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/300?text=No+Image')}
                />
              </div>
              
              {/* Content */}
              <div className="p-4">
                <span className={`text-xs font-bold px-2 py-1 rounded text-white mb-2 inline-block ${ad.section === 'sidebar' ? 'bg-purple-500' : 'bg-green-500'}`}>
                  {ad.section.toUpperCase()}
                </span>
                <h3 className="font-bold text-gray-800 text-lg truncate">{ad.title}</h3>
                <p className="text-sm text-gray-500 truncate">{ad.linkUrl}</p>
                
                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <button 
                    onClick={() => handleEdit(ad)}
                    className="flex-1 bg-blue-100 text-blue-700 py-2 rounded hover:bg-blue-200 font-medium text-sm"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(ad.id)}
                    className="flex-1 bg-red-100 text-red-700 py-2 rounded hover:bg-red-200 font-medium text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AddAdPage;